import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { BarcodeDisplay } from '../components/BarcodeDisplay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Printer, Search, Package, Plus, Minus, Trash2, ScanBarcode, X, Warehouse, AlignJustify } from 'lucide-react';
import { toast } from 'sonner';
import CalcInput from '../components/CalcInput';

// ─── Label size presets ───────────────────────────────────────────────────────
// Enter the two dimensions of your label in any order.
// The shorter side will automatically become the CSS page width (= print-head direction),
// and the longer side becomes the CSS page height (= feed direction).
// XP-470B "40×30" → CSS width=30mm, height=40mm (portrait roll).
const LABEL_SIZES = {
  '40x30':  { w:40,  h:30,  label:'40 × 30 mm (XP-470B standard)' },
  '50x30':  { w:50,  h:30,  label:'50 × 30 mm'                    },
  '60x40':  { w:60,  h:40,  label:'60 × 40 mm'                    },
  '75x50':  { w:75,  h:50,  label:'75 × 50 mm'                    },
  '50x80':  { w:50,  h:80,  label:'50 × 80 mm (Portrait)'         },
  '57x90':  { w:57,  h:90,  label:'57 × 90 mm (Portrait)'         },
  '100x50': { w:100, h:50,  label:'100 × 50 mm (Wide)'            },
  '100x150':{ w:100, h:150, label:'100 × 150 mm (Shipping)'       },
  'custom': { w:null, h:null, label:'Custom Size…', custom:true    },
};

// ─── Adaptive print parameters ────────────────────────────────────────────────
// Everything computed from width × height. No hardcoded lookup table.
// cssW = shorter side (print-head, horizontal), cssH = longer side (feed, vertical).
function computePrintParams(cssW, cssH) {
  const PX  = 3.779; // CSS px per mm at 96 dpi
  const PAD = 1;     // 1 mm padding all round

  // Name row: 3.5–6 mm
  const nameH    = Math.max(3.5, Math.min(6, cssH * 0.15));
  const nameFontMm = parseFloat((nameH * 0.72).toFixed(2));

  // Value row (bold barcode number): 4.5–7 mm
  const valH     = Math.max(4.5, Math.min(7, cssH * 0.13));
  const valFontMm  = parseFloat((valH * 0.72).toFixed(2));

  // Barcode area fills remaining height
  const bcW = cssW - PAD * 2;
  const bcH = cssH - PAD * 2 - nameH - valH;

  // JsBarcode pixel dimensions calibrated so SVG natural size ≈ fill area (minimal stretch)
  const fontSize  = Math.max(6, Math.min(14, Math.round(bcH * PX * 0.09)));
  const barcodeH  = Math.max(20, Math.round(bcH * PX) - fontSize - 1);
  const barcodeW  = Math.max(0.8, parseFloat((bcW * PX / 120).toFixed(2)));

  return { nameH, nameFontMm, valH, valFontMm, bcW, bcH, fontSize, barcodeH, barcodeW };
}

export default function BarcodePrintPage() {
  const { currentBranch } = useAuth();
  const [products, setProducts]           = useState([]);
  const [search, setSearch]               = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [printList, setPrintList]         = useState([]);
  const [labelSize, setLabelSize]         = useState('40x30');
  const [customW, setCustomW]             = useState(40);
  const [customH, setCustomH]             = useState(30);
  const [printMode, setPrintMode]         = useState('label');
  const [loading, setLoading]             = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const searchTimer = useRef(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/products', { params: { limit: 500, is_repack: false } });
      setProducts((res.data.products || []).filter(p => p.barcode));
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const loadFromInventory = async () => {
    if (!currentBranch?.id || currentBranch.id === 'all') { toast.error('Select a specific branch first'); return; }
    setLoadingInventory(true);
    try {
      const res = await api.get(`/products/barcode-inventory/${currentBranch.id}`);
      const inv = res.data.products || [];
      if (!inv.length) { toast.info('No products with barcodes found'); setLoadingInventory(false); return; }
      const list = inv.map(p => ({ product: p, qty: Math.max(1, Math.round(p.stock || 1)) }));
      setPrintList(list);
      toast.success(`Loaded ${inv.length} products (${list.reduce((s,i) => s+i.qty, 0)} labels)`);
    } catch { toast.error('Failed to load inventory'); }
    setLoadingInventory(false);
  };

  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/products', { params: { search, limit: 15, is_repack: false } });
        setSearchResults((res.data.products || []).filter(p => p.barcode));
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const addToPrintList = (product) => {
    setPrintList(prev => {
      const exists = prev.find(p => p.product.id === product.id);
      if (exists) return prev.map(p => p.product.id === product.id ? { ...p, qty: p.qty + 1 } : p);
      return [...prev, { product, qty: 1 }];
    });
    setSearch(''); setSearchResults([]);
  };
  const updateQty = (id, qty) => {
    if (qty <= 0) setPrintList(prev => prev.filter(p => p.product.id !== id));
    else setPrintList(prev => prev.map(p => p.product.id === id ? { ...p, qty } : p));
  };
  const removeFromList = (id) => setPrintList(prev => prev.filter(p => p.product.id !== id));
  const addAllInventory = () => {
    setPrintList(products.map(p => ({ product: p, qty: 1 })));
    toast.success(`Added ${products.length} products`);
  };

  const totalLabels = printList.reduce((sum, p) => sum + p.qty, 0);

  // ── Resolve dimensions ───────────────────────────────────────────────────────
  // Use the user's dimensions AS-IS. "40×30" means width=40mm, height=30mm (landscape).
  // DO NOT swap — the user knows their label orientation.
  const isCustom = labelSize === 'custom';
  const raw = isCustom
    ? { w: Number(customW) || 40, h: Number(customH) || 30 }
    : LABEL_SIZES[labelSize];

  const cssW = raw.w;
  const cssH = raw.h;

  const params = computePrintParams(cssW, cssH);

  // ── Print ────────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printList.length) { toast.error('Add products to print'); return; }

    // Open popup at exact pixel size of the label so the preview looks right.
    // Chrome still uses its configured paper size for actual printing — the user
    // must add a custom paper size in Windows matching cssW × cssH mm (see notice above).
    const PX = 3.779;
    const pw = Math.round(cssW * PX) + 40;
    const ph = Math.round(cssH * PX) + 100;
    const win = window.open('', '_blank', `width=${pw},height=${ph},scrollbars=no`);
    if (!win) { toast.error('Allow pop-ups to print'); return; }

    const { nameH, nameFontMm, valH, valFontMm, barcodeH, barcodeW } = params;
    const esc = s => s.replace(/[<>&'"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;',"'":'&#39;','"':'&quot;'}[c]));
    const isLabel = printMode === 'label';

    const html = printList.flatMap(({ product, qty }) =>
      Array.from({ length: qty }, (_, i) => `
        <div class="label">
          <div class="nm">${esc(product.name)}</div>
          <div class="bc"><svg class="sv" id="b${product.id.replace(/-/g,'')}${i}"></svg></div>
          <div class="vl">${esc(product.barcode)}</div>
        </div>
      `)
    ).join('');

    const js = printList.flatMap(({ product, qty }) =>
      Array.from({ length: qty }, (_, i) => {
        const sid = `b${product.id.replace(/-/g,'')}${i}`;
        return `try{JsBarcode('#${sid}','${product.barcode}',{format:'CODE128',width:${barcodeW},height:${barcodeH},displayValue:false,margin:0});}catch(e){}`;
      })
    ).join('\n');

    win.document.write(`<!DOCTYPE html><html><head><title>Barcode Labels</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.3/dist/JsBarcode.all.min.js"><\/script>
      <style>
        /* @page size hint: matches when Windows printer paper = ${cssW}×${cssH}mm */
        @page { size: ${cssW}mm ${cssH}mm; margin: 0; }
        * { margin:0; padding:0; box-sizing:border-box; }
        /* Anchoring html/body to label width prevents SVG width:100% resolving against A4 */
        html,body { width:${cssW}mm; overflow:hidden; font-family:Arial,sans-serif; }
        ${isLabel ? '' : `.wrap { display:flex; flex-wrap:wrap; gap:2mm; padding:2mm; }`}
        .label {
          width:${cssW}mm; height:${cssH}mm;
          display:flex; flex-direction:column; padding:1mm; overflow:hidden;
          ${isLabel ? 'break-after:page; page-break-after:always;' : 'page-break-inside:avoid;'}
        }
        .nm {
          font-size:${nameFontMm}mm; font-weight:700; text-align:center;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
          height:${nameH}mm; line-height:${nameH}mm; flex-shrink:0;
        }
        .bc { flex:1; min-height:0; overflow:hidden; display:flex; align-items:center; justify-content:center; }
        .sv { display:block; width:100%; height:100%; }
        .vl {
          height:${valH}mm; line-height:${valH}mm; font-size:${valFontMm}mm;
          font-weight:800; text-align:center; letter-spacing:0.06em;
          flex-shrink:0; overflow:hidden;
        }
      </style>
    </head><body>
      <div class="wrap">${html}</div>
      <script>
        ${js}
        document.querySelectorAll('.sv').forEach(function(s){
          s.removeAttribute('width'); s.removeAttribute('height');
          s.setAttribute('preserveAspectRatio','none');
        });
        setTimeout(function(){ window.print(); }, 700);
      <\/script>
    </body></html>`);
    win.document.close();
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="barcode-print-page">
      <div>
        <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily:'Manrope,sans-serif', color:'var(--text-primary)' }}>Print Barcodes</h1>
        <p className="text-sm text-slate-500 mt-1">Select products and quantity of labels to print</p>
      </div>

      {/* One-time Windows setup notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed" data-testid="printer-setup-notice">
        <strong>To fix "big grey area" and "jumps stickers" — do this once:</strong>
        <ol className="list-decimal list-inside mt-1 space-y-0.5">
          <li>Windows → <strong>Devices &amp; Printers</strong> → right-click <strong>Xprinter XP-470B</strong> → <strong>Printing Preferences</strong> → Paper/Media Size → add custom <strong>{cssW} × {cssH} mm</strong></li>
          <li>In the <strong>Chrome print dialog</strong>: More settings → <strong>Paper size = {cssW}×{cssH}mm</strong> · <strong>Scale = 100%</strong> · <strong>Uncheck Headers &amp; footers</strong></li>
        </ol>
        Once configured, each label = exactly 1 sticker with zero grey area.
      </div>

      {/* Settings bar */}
      <Card style={{ border:'1px solid var(--border-default)' }}>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">

            {/* Mode */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Mode</span>
              <div className="flex rounded-md overflow-hidden border border-slate-200" data-testid="print-mode-toggle">
                <button onClick={() => setPrintMode('label')} data-testid="mode-label-btn"
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${printMode==='label' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  Label Printer (XP-470B)
                </button>
                <button onClick={() => setPrintMode('sheet')} data-testid="mode-sheet-btn"
                  className={`px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors ${printMode==='sheet' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  Sheet / A4
                </button>
              </div>
            </div>

            <div className="h-6 border-l border-slate-200" />

            {/* Label size */}
            <div className="flex items-center gap-2 flex-wrap">
              <AlignJustify size={14} className="text-slate-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Size</span>
              <Select value={labelSize}
                onValueChange={v => { setLabelSize(v); if (v !== 'custom') { setCustomW(LABEL_SIZES[v].w); setCustomH(LABEL_SIZES[v].h); } }}>
                <SelectTrigger data-testid="label-size-select" className="h-8 text-xs w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LABEL_SIZES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isCustom && (
                <div className="flex items-center gap-1.5" data-testid="custom-size-inputs">
                  <input type="number" min={10} max={200} step={0.5} value={customW}
                    onChange={e => setCustomW(e.target.value)}
                    className="w-16 h-8 px-2 text-xs border border-slate-200 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]"
                    data-testid="custom-width-input" placeholder="mm" />
                  <span className="text-xs text-slate-400">×</span>
                  <input type="number" min={10} max={300} step={0.5} value={customH}
                    onChange={e => setCustomH(e.target.value)}
                    className="w-16 h-8 px-2 text-xs border border-slate-200 rounded-md text-center focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]"
                    data-testid="custom-height-input" placeholder="mm" />
                  <span className="text-xs text-slate-400">mm</span>
                </div>
              )}

              {/* Live computed size indicator */}
              <span className="text-[10px] font-mono text-slate-400 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                CSS: {cssW}×{cssH}mm · barH={params.barcodeH}px · barW={params.barcodeW}
              </span>
            </div>

            <div className="flex-1" />

            {/* Actions */}
            <Button variant="outline" size="sm" onClick={addAllInventory} data-testid="add-all-btn" disabled={!products.length}>
              <Package size={14} className="mr-1.5" /> Add All
            </Button>
            <Button variant="outline" size="sm" onClick={loadFromInventory} disabled={loadingInventory} data-testid="load-inventory-btn">
              <Warehouse size={14} className="mr-1.5 text-blue-500" />
              {loadingInventory ? 'Loading…' : 'Load from Inventory'}
            </Button>
            <Button onClick={handlePrint} disabled={!printList.length} size="sm"
              className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" data-testid="print-barcodes-btn">
              <Printer size={14} className="mr-2" /> Print {totalLabels} Label{totalLabels !== 1 ? 's' : ''}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Product search */}
      <Card style={{ border:'1px solid var(--border-default)' }}>
        <CardContent className="p-4">
          <Label className="text-sm font-semibold mb-2 block">Add Products to Print</Label>
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="barcode-product-search" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, SKU, or barcode…" className="pl-9 h-10" />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto z-50">
                {searchResults.map(p => (
                  <button key={p.id} onClick={() => addToPrintList(p)}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                    data-testid={`search-add-${p.id}`}>
                    <div>
                      <span className="font-medium text-sm">{p.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{p.sku}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{p.barcode}</Badge>
                      <Plus size={14} className="text-emerald-600" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Print list */}
      {printList.length > 0 ? (
        <Card style={{ border:'1px solid var(--border-default)' }}>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor:'#F1F0ED' }}>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Product</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Barcode</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Preview</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium text-center w-40">Qty</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {printList.map(({ product, qty }) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-slate-400">{product.sku} · {product.category}</div>
                    </TableCell>
                    <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded">{product.barcode}</code></TableCell>
                    <TableCell><BarcodeDisplay value={product.barcode} width={1} height={28} fontSize={9} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => updateQty(product.id, qty-1)} data-testid={`qty-minus-${product.id}`} className="h-8 w-8 p-0"><Minus size={12}/></Button>
                        <CalcInput value={qty} onChange={v => updateQty(product.id, parseInt(v)||1)} className="w-16 h-8 text-center text-sm" data-testid={`qty-input-${product.id}`} />
                        <Button variant="outline" size="sm" onClick={() => updateQty(product.id, qty+1)} data-testid={`qty-plus-${product.id}`} className="h-8 w-8 p-0"><Plus size={12}/></Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => removeFromList(product.id)} className="text-red-500" data-testid={`remove-${product.id}`}><Trash2 size={14}/></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
              <span className="text-sm text-slate-500">{printList.length} product(s) · {totalLabels} label(s) · {isCustom ? `${customW}×${customH}mm` : LABEL_SIZES[labelSize].label} · CSS {cssW}×{cssH}mm</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPrintList([])} data-testid="clear-print-list"><X size={14} className="mr-1"/>Clear All</Button>
                <Button onClick={handlePrint} size="sm" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" data-testid="print-bottom-btn"><Printer size={14} className="mr-1.5"/>Print Labels</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-12 text-center">
            <ScanBarcode size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No products added yet</p>
            <p className="text-sm text-slate-400 mt-1">Search above or click "Add All" to print your entire inventory</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
